import React, { Fragment, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Chart, ZoomButtons } from 'react-stockcharts';
import { Label } from 'react-stockcharts/lib/annotation';
import { CrossHairCursor, CurrentCoordinate } from 'react-stockcharts/lib/coordinates';
import { discontinuousTimeScaleProvider } from 'react-stockcharts/lib/scale';
import { AreaSeries, LineSeries } from 'react-stockcharts/lib/series';
import * as d3 from 'd3';
import { scaleLinear, scaleLog } from 'd3-scale'; // Import D3 scaleLog
import { curveMonotoneX } from 'd3-shape';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import { last } from 'react-stockcharts/lib/utils';
import { t } from 'i18next';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { hexToRGBA } from 'react-stockcharts/lib/utils';

import { chartColors, englishLocale, germanLocale } from 'shared/constants/constants';
import { State } from 'shared/interface';
import localizationConstants from 'shared/util/translation/constants';
import { Translate } from 'shared/util/translation/translate';
import * as actionTypes from 'store/action-types';
import { ChartAxisPosition, ForeCastType, ScaleType } from 'shared/interface/enums';
import {
	calculateAverages,
	chartTimeRangeMapper,
	cleanForecastItemName,
	createAction,
	customNumberFormatter,
	debounce,
	formatDate,
	getChartDataBasedOnSpan,
	getColorForDataType,
	getDataTypeDisplayName,
	getDataTypeValue,
	getDateFormatWithoutYear,
	renderTicks
} from 'shared/util/utility';

import ChartCanvas from 'features/futuretable/util/chartCanvas';
import compare from 'features/futuretable/util/compare';
import { getChartDataBasedOnIndicators, getIndicatorsWindowSize } from 'features/futuretable/util/chartUtility';
import { IChartData, IChartDesignConfig, IChartDetails, IChartSettingsType, ISpan, IChartItem } from '../interface/futureTable';
import ChartAxis from './chartAxis';
import CommonChart from './CommonChart';
import ChangeChartColorPopup from './changeChartColorPopup';
import HoverTooltip from './hoverTooltip';
import HoverTooltipSVG from './hoverTooltipSVG';
import { RenderExternalIndicators } from './renderExternalIndicators';
import RenderIndicatorsOnchart from './renderIndicatorsOnchart';
import TooltipBackgroundShapeSVG from './tooltipBackgroundShapeSVG';

interface IForecastChartItemProps extends IChartItem {
	source: string;
}

interface IProps {
	chartData: IChartData[];
	chartType: string;
	showZoomButton: boolean;
	loading: boolean;
	chartDetails: IChartDetails;
	cardIndex: number;
	chartDesignConfig: IChartDesignConfig;
	spanObj: ISpan;
	chartName: string;
	chartSettings: IChartSettingsType[];
	scaleType: string;
	isIntraday?: boolean;
	setChartColor?: (color: string) => void;
	materialIndex?: number;
	staticHeight?: boolean;
	allChartDetails: { [key: string]: IChartDetails };
	barWidth?: { [key: string]: number };
	setBarWidth?: (width: { [key: string]: number }) => void;
	forecastKeys?: string[];
	yAxisFontSize?: number;
	xAxisFontSize?: number;
	selectedDataTypesPerMaterial?: { [materialKey: string]: string[] };
	selectedFrequencies?: { [materialKey: string]: string };
	setIsExportDisabled?: (value: boolean) => void;
}

export interface ISTOIndicatorType {
	datum: number;
	tage: number;
	k: number;
}

export interface IMACDIndicatorInputType {
	kurz: number;
	signal: number;
	lang: number;
}

export interface IIndicatorConfig {
	keyName: string;
	indicatorKeyName: string;
	windowSize: number | ISTOIndicatorType | IMACDIndicatorInputType;
	value: string;
}

export interface IIndicatorObject {
	macd: {
		[key: string]: number;
	};
	sto: {
		[key: string]: number;
	};
}

interface IForecastChartItemProps extends IChartItem {
	source: string;
}

const chartTopPadding = 50;

const StockChart: React.FC<IProps> = (props) => {
	const {
		chartData,
		chartType,
		spanObj: { span, endDate, startDate },
		chartDetails,
		cardIndex,
		chartDesignConfig,
		showZoomButton,
		chartSettings,
		scaleType,
		loading,
		setChartColor,
		chartName,
		materialIndex,
		staticHeight,
		allChartDetails,
		barWidth,
		setBarWidth,
		forecastKeys,
		yAxisFontSize,
		xAxisFontSize,
		selectedDataTypesPerMaterial,
		selectedFrequencies,
		setIsExportDisabled
	} = props;

	const [containerWidth, setContainerWidth] = useState(0);
	const [containerHeight, setContainerHeight] = useState(0);
	const [suffix, setSuffix] = useState(0);
	const language = useSelector((state: State) => ({ ...state.siteConfig }.language));
	const dateFormat = useSelector((state: State) => state.siteConfig.dateFormat);
	const [action, setAction] = useState('');
	const location = useLocation();
	const dispatch = useDispatch();
	const isIframe = location.pathname.includes('iframe');

	const chartContainerElement = document.getElementById(`chart-container-${chartName}-${chartDetails.symbol}-${cardIndex}`);

	const totalIndicatorsLength = useMemo(() => {
		return chartSettings.reduce((acc, chart) => {
			return acc + chart.indicators.filter((indi) => ['atr', 'rsi', 'sto', 'macd'].includes(indi.value)).length;
		}, 0);
	}, [chartSettings]);

	const calculateHeight = useMemo(() => {
		return (cardIndex < 2 ? 135 : 100) * totalIndicatorsLength;
	}, [cardIndex, totalIndicatorsLength]);

	const handleResize = useCallback(
		(entries: ResizeObserverEntry[]) => {
			if (!chartContainerElement) return;

			const { width, height } = entries[0].contentRect;

			// Only update state if there is a significant change in dimensions
			setContainerWidth((cw) => (width !== cw && Math.abs(cw - width) > 15 ? width : cw));

			setContainerHeight((ch) => (ch === 0 && height !== ch && Math.abs(ch - height) > 15 ? height : ch));
		},
		[chartContainerElement]
	);

	const throttleResizeFun = useCallback(debounce(handleResize, 50), [handleResize]);
	const { symbol, boerse } = chartDetails;

	const colorList =
		!isEmpty(allChartDetails) && allChartDetails[`${symbol}-${boerse}-0`] && allChartDetails[`${symbol}-${boerse}-0`].sources !== undefined
			? allChartDetails[`${symbol}-${boerse}-0`]?.sources?.reduce((acc: Record<string, string>, key: string, index: number) => {
					acc[key] = chartColors[index];
					return acc;
			  }, {} as Record<string, string>)
			: {};

	useEffect(() => {
		if (!chartContainerElement) return;

		const resizeObserver = new ResizeObserver(throttleResizeFun);
		resizeObserver.observe(chartContainerElement);

		return () => {
			resizeObserver.unobserve(chartContainerElement);
		};
	}, [chartContainerElement, throttleResizeFun]);

	const compareKeys = useMemo(() => {
		const keys: string[] = [];
		let cumulativeIndex = 0;
		chartSettings.forEach((cs, idx) => {
			const materialKey = `${cs.symbol}-${cs.boerse}-${materialIndex || idx}`;
			const fields: string[] = Array.isArray((cs as any).fields) ? ((cs as any).fields as string[]) : [];
			const selected = selectedDataTypesPerMaterial?.[materialKey];
			const dataTypes = selected && selected.length > 0 ? selected : fields.length > 0 ? [fields[0]] : ['l'];
			dataTypes.forEach((dt) => {
				const localIdx = Math.max(0, fields.indexOf(dt));
				const seriesIdx = cumulativeIndex + localIdx;
				keys.push(`cv${cs.symbol}-${cs.boerse}-${seriesIdx}`);
			});
			cumulativeIndex += dataTypes.length;
		});
		return keys;
	}, [chartSettings, selectedDataTypesPerMaterial, materialIndex]);

	const indicatorsFunctionNames = [] as IIndicatorConfig[];
	chartSettings.map((chart, index) => {
		if (chart.indicators.length > 0) {
			chart.indicators.map((indi) => {
				indicatorsFunctionNames.push({
					indicatorKeyName: `${indi.value}-${chart.symbol}-${chart.boerse}-${chart?.id || ''}`,
					keyName: `${
						scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL
							? indi.value === 'atr' || indi.value === 'sto'
								? 'cv'
								: 'percentage'
							: indi.value === 'atr' || indi.value === 'sto'
							? 'cv'
							: 'price'
					}${chart.symbol}-${chart.boerse}-${index}`,
					windowSize: getIndicatorsWindowSize(indi),
					value: indi.value
				});
			});
		}
	});

	const compareCalculator = compare()
		.options({
			basePath: [...compareKeys, ...indicatorsFunctionNames.map((indi) => indi.indicatorKeyName)],
			mainKeys: [...compareKeys, ...indicatorsFunctionNames.map((indi) => indi.indicatorKeyName)],
			compareKeys: [...compareKeys, ...indicatorsFunctionNames.map((indi) => indi.indicatorKeyName)]
		})
		.accessor((d: any) => {
			d.compare;
		})
		.merge((d: any, c: any) => {
			d.compare = {
				...c
			};
		});

	const xScaleProvider = discontinuousTimeScaleProvider
		.inputDateAccessor((d: any) => {
			return moment(d.date).toDate();
		})
		.setLocale(language == 'en' ? englishLocale : germanLocale);

	const chartDataBasedOnType = useMemo(() => {
		if (chartType !== 'bar') {
			// For line and area charts, apply grouping frequency logic
			if (!chartData.length) {
				return chartData;
			}

			// Check if we have selected frequencies that differ from the default
			const hasCustomFrequencies = selectedFrequencies && Object.values(selectedFrequencies).some((freq) => freq && freq !== '1d');

			if (hasCustomFrequencies) {
				// Apply grouping based on selected frequencies
				const averages = calculateAverages([...chartData], chartDetails?.notierung);

				// Map frequency to the appropriate average data
				const frequencyToAverageMap: { [key: string]: any[] } = {
					'1d': averages.dailyAverage,
					'1w': averages.weeklyAverage,
					'1m': averages.monthlyAverage,
					'1y': averages.yearlyAverage
				};

				// Helper to normalize to span codes
				const normalizeToSpan = (val?: string) => {
					switch (val) {
						case 'D':
						case '1d':
							return '1d';
						case 'W':
						case '1w':
							return '1w';
						case 'M':
						case '1m':
							return '1m';
						case 'Y':
						case '1y':
							return '1y';
						default:
							return '1d';
					}
				};

				// Build date -> averagedRow maps for each span
				const buildDateMapForSpan = (rows: any[], spanCode: '1d' | '1w' | '1m' | '1y') => {
					const map = new Map<string, any>();
					rows.forEach((row) => {
						const base = moment(row.date);
						let start = base.clone();
						let end = base.clone();
						switch (spanCode) {
							case '1w':
								start = base.clone().startOf('isoWeek');
								end = base.clone().endOf('isoWeek');
								break;
							case '1m':
								start = base.clone().startOf('month');
								end = base.clone().endOf('month');
								break;
							case '1y':
								start = base.clone().startOf('year');
								end = base.clone().endOf('year');
								break;
							default:
								start = base.clone();
								end = base.clone();
						}
						const cur = start.clone();
						while (cur.isSameOrBefore(end, 'day')) {
							map.set(cur.format('YYYY-MM-DD'), row);
							cur.add(1, 'day');
						}
					});
					return map;
				};

				const map1d = buildDateMapForSpan(frequencyToAverageMap['1d'] || [], '1d');
				const map1w = buildDateMapForSpan(frequencyToAverageMap['1w'] || [], '1w');
				const map1m = buildDateMapForSpan(frequencyToAverageMap['1m'] || [], '1m');
				const map1y = buildDateMapForSpan(frequencyToAverageMap['1y'] || [], '1y');

				// Apply per-material frequency selection while preserving all dates
				return chartData.map((originalPoint) => {
					const dateKey = moment(originalPoint.date).format('YYYY-MM-DD');
					const mergedPoint: any = { ...originalPoint };
					let cumulativeIndex = 0;
					chartSettings.forEach((cs, idx) => {
						const matKeyIndex = typeof materialIndex === 'number' ? materialIndex : idx;
						const matKey = `${cs.symbol}-${cs.boerse}-${matKeyIndex}`;
						const rawFreq = selectedFrequencies && selectedFrequencies[matKey];
						const freq = normalizeToSpan(rawFreq);
						const avgRow =
							freq === '1w'
								? map1w.get(dateKey)
								: freq === '1m'
								? map1m.get(dateKey)
								: freq === '1y'
								? map1y.get(dateKey)
								: map1d.get(dateKey);
						const selectedTypes = selectedDataTypesPerMaterial?.[matKey];
						const fieldsCount =
							selectedTypes && selectedTypes.length > 0 ? selectedTypes.length : Array.isArray(cs.fields) ? cs.fields.length : 1;
						const startIndex = cumulativeIndex;
						if (avgRow) {
							for (let j = 0; j < fieldsCount; j++) {
								const globalIdx = startIndex + j;
								const cvKey = `cv${cs.symbol}-${cs.boerse}-${globalIdx}`;
								const priceKey = `price${cs.symbol}-${cs.boerse}-${globalIdx}`;
								const percentageKey = `percentage${cs.symbol}-${cs.boerse}-${globalIdx}`;
								if ((avgRow as any)[cvKey] && (mergedPoint as any)[cvKey]) {
									(mergedPoint as any)[cvKey] = {
										...((mergedPoint as any)[cvKey] || {}),
										l: (avgRow as any)[cvKey]?.l
									};
								}
								if ((avgRow as any)[priceKey] !== undefined) {
									(mergedPoint as any)[priceKey] = (avgRow as any)[priceKey];
								}
								if ((avgRow as any)[percentageKey] !== undefined) {
									(mergedPoint as any)[percentageKey] = (avgRow as any)[percentageKey];
								}
							}

							// If this material is using weekly frequency, attach week number once
							if (freq === '1w' && mergedPoint.week === undefined) {
								mergedPoint.week = (avgRow as any)?.week ?? moment(originalPoint.date).isoWeek();
							}
						}
						cumulativeIndex += fieldsCount;
					});
					// keep original x position
					mergedPoint.date = originalPoint.date;
					return mergedPoint;
				});
			}

			return chartData;
		}

		// Early return if no data
		if (!chartData.length) {
			return [];
		}

		const spanValue = chartTimeRangeMapper(chartData)[span as string];
		const averages = calculateAverages([...chartData], chartDetails?.notierung);
		const data = getChartDataBasedOnSpan(spanValue, averages);

		// Determine if we are on the dashboard and get the preferred forecast source
		// const isDashboard = location.pathname.includes('/dashboard');
		// const preferredSource = chartDetails.sources && chartDetails.sources.length > 0 ? chartDetails.sources[0] : undefined;

		// Process data in a single pass with optimized filtering
		return data;
	}, [chartData, chartDetails?.notierung, chartType, span, selectedFrequencies, selectedDataTypesPerMaterial, materialIndex]);

	// Optimize memoized chart data calculation
	const memoizedChartData = useMemo(() => {
		if (chartType !== 'bar') {
			return chartDataBasedOnType;
		}

		// Filter out zero values more efficiently
		return chartDataBasedOnType.filter((item: IChartData) => {
			return Object.keys(item).some((key) => {
				if (!key.startsWith('cv')) return false;

				const value = item[key];
				if (typeof value !== 'object' || value === null) return false;

				const chartItem = value as IChartItem;
				return chartItem.e !== 0 || chartItem.h !== 0 || chartItem.l !== 0 || chartItem.t !== 0;
			});
		});
	}, [chartDataBasedOnType, chartType]);

	// Memoize the calculated data with optimized dependencies
	const calculatedData = useMemo(
		() => getChartDataBasedOnIndicators(compareCalculator(memoizedChartData), indicatorsFunctionNames, scaleType),
		[memoizedChartData, compareCalculator, indicatorsFunctionNames, scaleType]
	);
	const { data, xScale, xAccessor, displayXAccessor } = xScaleProvider(calculatedData);
	location.pathname.includes('/dashboard') ? (cardIndex > 1 ? 3 : 10) : 23;

	const getLastActualPrice = (data: IChartData[]): IChartData | null => {
		const actualPrices = data.filter((item: IChartData) => (item[`cv${symbol}-${boerse}-0`] as IChartItem)?.type === 'actualPrice');
		return actualPrices.length > 0 ? actualPrices[actualPrices.length - 1] : null;
	};

	useEffect(() => {
		const { symbol, boerse } = chartDetails;
		const forecastType = calculatedData.filter((item) => item[`cv${symbol}-${boerse}-0`]?.type === ForeCastType.FORECASTED_PRICE);

		const lastActualPrice = getLastActualPrice(calculatedData);
		dispatch(
			createAction(actionTypes.GET_CHART_LAST_OBJ, {
				key: [`${symbol}-${boerse}`],
				data: forecastType && !isEmpty(forecastType) ? lastActualPrice : calculatedData[calculatedData.length - 1]
			})
		);
	}, []);

	const getXextentForBarChart = useCallback(() => {
		switch (span) {
			case '1d':
				return xAccessor(data[Math.max(0, data.length - (location.pathname.includes('/dashboard') ? (cardIndex > 1 ? 3 : 10) : 30))]);
			case '1w':
				return xAccessor(data[Math.max(0, data.length - (location.pathname.includes('/dashboard') ? (cardIndex > 1 ? 3 : 10) : 24))]);
			case '1m':
				return xAccessor(data[Math.max(0, data.length - (location.pathname.includes('/dashboard') ? (cardIndex > 1 ? 3 : 10) : 18))]);
			case '1y':
				return location.pathname.includes('/dashboard') && cardIndex > 1 ? xAccessor(data[Math.max(0, data.length - 3)]) : xAccessor(data[0]);
			default:
				return xAccessor(data[0]);
		}
	}, [cardIndex, data, location.pathname, span, xAccessor]);

	const xExtents = useMemo(() => {
		if (startDate && endDate) {
			const startIndex = data.findIndex((ele: any) => formatDate(ele.date, dateFormat) === formatDate(startDate, dateFormat));
			const endIndex = data.findIndex((ele: any) => formatDate(ele.date, dateFormat) === formatDate(endDate, dateFormat));
			return [startIndex === -1 ? xAccessor(data[0]) : startIndex, endIndex === -1 ? xAccessor(last(data)) : endIndex];
		} else {
			const start = xAccessor(last(data));

			const end = chartType === 'bar' ? getXextentForBarChart() : xAccessor(data[0]);
			return [start, end];
		}
	}, [chartType, startDate, endDate, data, getXextentForBarChart, xAccessor]);

	// Determine whether to use 'date' or 'month' for x-axis labels
	const shouldUseDate = useMemo(() => {
		return Object.values(allChartDetails).some((detail: IChartDetails) => detail?.notierung === 'D' || detail?.notierung === 'W');
	}, [allChartDetails]);

	const toolTipContent = (ys: any) => {
		moment.updateLocale(language, language === 'de' ? germanLocale : englishLocale);
		const month = moment(ys.currentItem.date).format('MMMM');
		const year = moment(ys.currentItem.date).format('YYYY');

		// Check if history is added and chart settings length is greater than 1
		const hasHistory = props.chartSettings.some((chart) => chart.history.value !== '');
		const shouldShowDateWithoutYear = hasHistory && props.chartSettings.length > 1;

		// Create date format based on conditions
		let dateFormatToUse = dateFormat;
		if (shouldShowDateWithoutYear) {
			dateFormatToUse = getDateFormatWithoutYear(dateFormat);
		}

		const currentDate = moment(ys.currentItem.date).format(props.isIntraday ? `${dateFormatToUse} hh:mm A` : dateFormatToUse);

		const normalizeToSpan = (val?: string) => {
			switch (val) {
				case 'D':
				case '1d':
					return '1d';
				case 'W':
				case '1w':
					return '1w';
				case 'M':
				case '1m':
					return '1m';
				case 'Y':
				case '1y':
					return '1y';
				default:
					return undefined;
			}
		};

		const primarySelectionKey = `${chartDetails.symbol}-${chartDetails.boerse}-${materialIndex || 0}`;
		const selectedSpanForNonBar =
			chartType !== 'bar' && selectedFrequencies ? normalizeToSpan(selectedFrequencies[primarySelectionKey]) : undefined;
		const dateBySelectedNonBar =
			selectedSpanForNonBar === '1d'
				? currentDate
				: selectedSpanForNonBar === '1w'
				? `${t(localizationConstants.week)} ${ys.currentItem.week}`
				: selectedSpanForNonBar === '1m'
				? `${month}-${year}`
				: selectedSpanForNonBar === '1y'
				? `${year}`
				: currentDate;

		const date =
			chartType !== 'bar' && dateBySelectedNonBar
				? dateBySelectedNonBar
				: chartDetails.notierung === 'M' && chartType !== 'bar'
				? `${month}-${year}`
				: chartType === 'bar' && span === '1d'
				? currentDate
				: chartType === 'bar' && span === '1w'
				? `${t(localizationConstants.week)} ${ys.currentItem.week}`
				: chartType === 'bar' && span === '1m'
				? `${month} ${shouldShowDateWithoutYear ? '' : `-${year}`}`
				: chartType === 'bar' && span === '1y'
				? `${year}`
				: currentDate;

		// Determine if we are on the dashboard and get the preferred forecast source
		const isDashboard = location.pathname.includes('/dashboard');
		const preferredSource = chartDetails.sources && chartDetails.sources.length > 0 ? chartDetails.sources[0] : undefined;

		return {
			x: [
				`${
					props.chartSettings.length > 1 && chartType !== 'bar'
						? // Keep existing multi-material fallback when no override is provided
						  shouldUseDate
							? currentDate
							: hasHistory
							? month
							: `${month}-${year}`
						: date
				}`
			],
			y:
				!isEmpty(props.chartSettings) &&
				props.chartSettings
					?.map((ele, index) => {
						// Resolve base series index for this material using first selected field (or first available field)
						const materialKey = `${ele.symbol}-${ele.boerse}-${materialIndex || index}`;
						const selectedTypesForMaterial = selectedDataTypesPerMaterial?.[materialKey];
						const primaryKeyForMaterial =
							selectedTypesForMaterial && selectedTypesForMaterial.length > 0
								? selectedTypesForMaterial[0]
								: Array.isArray(ele.fields) && ele.fields.length > 0
								? ele.fields[0]
								: 'l';
						const fieldsForMaterial = Array.isArray(ele.fields) ? ele.fields : [];
						const localPrimaryIdxForMaterial = Math.max(0, fieldsForMaterial.indexOf(primaryKeyForMaterial));
						const startIndexForMaterial = seriesStartIndexMap[`${ele.id}`] || 0;
						const baseSeriesIdx = startIndexForMaterial + localPrimaryIdxForMaterial;
						// Skip processing if the data doesn't exist (e.g., locked materials)
						const currentItemData = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${baseSeriesIdx}`];
						if (!currentItemData) {
							return null;
						}

						const name =
							(language === 'en' &&
								allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.name_eng !== '' &&
								allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.name_eng) ||
							allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.name;
						const isShowUnitCurrency =
							ele.currency.value !== null && ele.currency.value !== '' && ele.unit.value !== null && ele.unit.value !== '';
						const currency = isShowUnitCurrency ? ele.currency.value : '';
						const unit = isShowUnitCurrency ? ele.unit.value : '';
						const showCurrencyUnit = isShowUnitCurrency ? `${currency} / ${unit}` : '';
						const showNameCurrencyUnit = isShowUnitCurrency ? `[${ele.currency.value} / ${ele.unit.value}]` : '';
						const isShowLabel =
							ys.currentItem.date === ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${baseSeriesIdx}`]?.d || chartType === 'bar';
						let value = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${baseSeriesIdx}`];
						const forecastData = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${baseSeriesIdx}`]?.source_data;
						// Only use the preferred forecast source on dashboard
						const sourceData: Record<string, IForecastChartItemProps> = {};
						if (forecastData) {
							if (isDashboard && preferredSource) {
								if (forecastData[preferredSource]) {
									sourceData[preferredSource] = forecastData[preferredSource];
								}
							} else {
								Object.keys(colorList).forEach((key) => {
									if (forecastData[key]) {
										sourceData[key] = forecastData[key];
									}
								});
							}
						}
						if (sourceData && typeof sourceData === 'object' && value && value.type !== ForeCastType.ACTUAL_PRICE) {
							Object.keys(sourceData).forEach((key) => {
								const nestedObject = sourceData[key];
								if (nestedObject && nestedObject.source) {
									if (key === nestedObject.source) {
										value = nestedObject?.l;
									}
								}
							});
						} else {
							value = value?.l;
						}

						const finalPrice = value
							? `${customNumberFormatter(
									value?.l === undefined ? value : value?.l,
									allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.nkstellen
							  )} ${showCurrencyUnit} `
							: '';
						const forecastType = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${baseSeriesIdx}`]?.type;
						const isBlurred = chartDetails.forecastAccess === 0 && forecastType === ForeCastType.FORECASTED_PRICE;
						const chartHistoryYear = props.chartSettings.length > 1 && ele.history.value ? ` - ${ele.history.value}` : '';
						const isForecastAccessKey = allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.forecastAccess;

						const indicatorValue = ele.indicators
							.filter((i) => !['atr', 'rsi', 'macd', 'sto'].includes(i.value))
							.map((indi, index) => {
								const indiValue = ys.currentItem[`${indi.value}-${ele.symbol}-${ele.boerse}-${ele.id}`];
								const opacity = (100 - (index + 1) * 10) / 100;
								const indicatorColor = hexToRGBA(ele.color, opacity);
								return {
									label:
										indi.value === 'bb'
											? `${indi.value.toUpperCase()} (close, ${indi.calculatedValue}, sma)`
											: `${indi.value.toUpperCase()} (${indi.calculatedValue})`,
									value:
										indi.value === 'bb'
											? `${indiValue?.top ? customNumberFormatter(indiValue.top) : '     '} ${
													indiValue?.middle ? customNumberFormatter(indiValue?.middle) : '     '
											  } ${indiValue?.bottom ? customNumberFormatter(indiValue?.bottom) : '     '}`
											: indiValue
											? customNumberFormatter(indiValue)
											: '     ',
									stroke: indicatorColor
								};
							});
						if (forecastType === ForeCastType.ACTUAL_PRICE) {
							// Check if we should show multiple data types
							const shouldShowMultipleDataTypes =
								/* props.chartSettings.length > 1 && */ selectedDataTypesPerMaterial &&
								selectedDataTypesPerMaterial[`${ele.symbol}-${ele.boerse}-${materialIndex || index}`] &&
								selectedDataTypesPerMaterial[`${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.length > 0;
							if (shouldShowMultipleDataTypes) {
								// Show multiple data types as separate lines in tooltip
								const dataTypeValues =
									selectedDataTypesPerMaterial &&
									selectedDataTypesPerMaterial[`${ele.symbol}-${ele.boerse}-${materialIndex || index}`]
										.map((dataType, index) => {
											const fields = Array.isArray(ele.fields) ? ele.fields : [];
											const localIdx = Math.max(0, fields.indexOf(dataType));
											const startIndex = seriesStartIndexMap[`${ele.id}`] || 0;
											const seriesIdx = startIndex + localIdx;
											const seriesData = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
											const value = seriesData?.l;
											const opacity = (100 - (index + 1) * 10) / 100;
											const chartGroupingColor = hexToRGBA(ele.color, opacity);
											const isMainFieldSelected =
												selectedDataTypesPerMaterial[`${ele.symbol}-${ele.boerse}-${materialIndex || index}`]?.length === 1 &&
												chartDetails?.originalField === getDataTypeDisplayName(dataType);

											if (value !== null && value !== undefined) {
												return {
													label: `${name} ${showNameCurrencyUnit}`,
													value: `${customNumberFormatter(
														value,
														allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.nkstellen
													)} ${showCurrencyUnit} ${chartHistoryYear} ${
														isMainFieldSelected ? '' : `- ${getDataTypeDisplayName(dataType)}`
													}`,
													stroke:
														selectedDataTypesPerMaterial[`${ele.symbol}-${ele.boerse}-${materialIndex || index}`]
															?.length > 0
															? chartGroupingColor
															: ele.color || '',
													indicator: indicatorValue,
													isShowLabel: props.isIntraday ? isShowLabel : true,
													isValueBlur: isBlurred,
													isMultiTooltip: false,
													forecastType: forecastType
												};
											}
											return null;
										})
										.filter(Boolean);

								// If no valid data types found, fall back to default behavior
								if (dataTypeValues.length === 0) {
									return {
										label: `${name} ${showNameCurrencyUnit}`,
										value: `${finalPrice}${chartHistoryYear}`,
										stroke: ele.color || '',
										indicator: indicatorValue,
										isShowLabel: props.isIntraday ? isShowLabel : true,
										isValueBlur: isBlurred,
										isMultiTooltip: false,
										forecastType: forecastType
									};
								}

								// Return multiple data type values as separate objects
								return dataTypeValues;
							}

							// Default behavior for single data type or no selected types
							const materialKey = `${ele.symbol}-${ele.boerse}-${materialIndex || index}`;
							const selectedTypes = selectedDataTypesPerMaterial?.[materialKey];
							const primaryKey =
								selectedTypes && selectedTypes.length > 0
									? selectedTypes[0]
									: Array.isArray(ele.fields) && ele.fields.length > 0
									? ele.fields[0]
									: 'l';
							const fields = Array.isArray(ele.fields) ? ele.fields : [];
							const localPrimaryIdx = Math.max(0, fields.indexOf(primaryKey));
							const startIndexSingle = seriesStartIndexMap[`${ele.id}`] || 0;
							const primaryIdx = startIndexSingle + localPrimaryIdx;
							const primaryData = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${primaryIdx}`];
							const primaryVal = primaryData?.l;
							const finalPriceLocal = primaryVal
								? `${customNumberFormatter(
										primaryVal,
										allChartDetails[`${ele.symbol}-${ele.boerse}-${index}`]?.nkstellen
								  )} ${showCurrencyUnit} `
								: '';
							return {
								label: `${name} ${showNameCurrencyUnit}`,
								value: `${finalPriceLocal}${chartHistoryYear}`,
								stroke: ele.color || '',
								indicator: indicatorValue,
								isShowLabel: props.isIntraday ? isShowLabel : true,
								isValueBlur: isBlurred,
								isMultiTooltip: false,
								forecastType: forecastType
							};
						} else {
							const currentData = ys.currentItem[`cv${ele.symbol}-${ele.boerse}-${baseSeriesIdx}`];
							let label = currentData;
							let value = currentData;
							if (sourceData && typeof sourceData === 'object' && label && label.type !== ForeCastType.ACTUAL_PRICE) {
								const mappedData = Object.keys(sourceData).map((item) => {
									return item;
								});
								const mappedValue = Object.keys(sourceData)
									.map((key) => {
										const nestedObject = sourceData[key];
										if (nestedObject) {
											// Handle both data structures: with source property and without (bar chart)
											const sourceKey = nestedObject.source || key;
											return {
												key: sourceKey,
												value: nestedObject?.l
											};
										}
									})
									.filter(Boolean); // Filter out undefined values
								value = mappedValue;
								label = mappedData;
							}

							return {
								label: `${name} ${showNameCurrencyUnit}`,
								forecastKeyLabel:
									props.chartSettings.length > 1
										? []
										: label.map((item: string) => {
												const cleanedItemName = cleanForecastItemName(item);
												return `${isForecastAccessKey === 1 ? cleanedItemName : ''}`;
										  }),
								value:
									props.chartSettings.length > 1
										? [`${finalPrice}${chartHistoryYear}`]
										: (value as { key: string; value: number }[])
												.filter((item) => item && item.key && label.includes(item.key))
												.map((item) => `${customNumberFormatter(item.value)} ${showCurrencyUnit}`),
								stroke: props.chartSettings.length > 1 ? ele.color || '' : label.map((item: string) => ele.forecastedColor[item]),
								indicator: indicatorValue,
								isShowLabel: props.isIntraday ? isShowLabel : true,
								isValueBlur: isBlurred,
								isMultiTooltip: !isEmpty(sourceData) && sourceData !== undefined && forecastType ? true : false,
								forecastType: forecastType
							};
						}
					})
					.filter(Boolean)
					.flat() // Flatten the array to handle multiple data type values
		};
	};

	const handleChangeChartColor = (color: string) => {
		setChartColor && setChartColor(color);
		setAction('');
	};

	const isZoomEnabled = chartType !== 'bar';

	const getBarChartYAccsesor = useMemo((): { yAxisLeftAccsessor: any[]; yAxisRightAccessor: any[] } => {
		const barChartLeftAxisYAccsesor: any = [];
		const barChartRightAxisYAccsesor: any = [];

		// Build local start-index map for flattened bar series (per selected fields)
		const localStartIndexMap: Record<string, number> = {};
		let cumulativeIndex = 0;
		chartSettings.forEach((chart, idx) => {
			const materialKey = `${chart.symbol}-${chart.boerse}-${materialIndex || idx}`;
			const selected = selectedDataTypesPerMaterial?.[materialKey];
			localStartIndexMap[`${chart.id}`] = cumulativeIndex;
			const count = selected && selected.length > 0 ? selected.length : 1; // default to 1 ('l')
			cumulativeIndex += count;
		});

		chartSettings.map((chart, index) => {
			const materialKey = `${chart.symbol}-${chart.boerse}-${materialIndex || index}`;
			const selectedDataTypes = selectedDataTypesPerMaterial?.[materialKey];
			// Use default 'l' if no data types are selected or if the array is empty
			const dataTypesToUse = selectedDataTypes && selectedDataTypes.length > 0 ? selectedDataTypes : ['l'];
			const fields: string[] = Array.isArray((chart as any).fields) ? ((chart as any).fields as string[]) : [];
			const startIndexForChart = localStartIndexMap[`${chart.id}`] || 0;

			if (chart.axis.value === ChartAxisPosition.LEFT_AXIS) {
				dataTypesToUse.forEach((dataType) => {
					barChartLeftAxisYAccsesor.push((d: any) => {
						const localIdx = Math.max(0, fields.indexOf(dataType));
						const seriesIdx = startIndexForChart + localIdx;
						const data = d[`cv${chart.symbol}-${chart.boerse}-${seriesIdx}`];
						if (!data) return NaN;
						if (data.type === ForeCastType.FORECASTED_PRICE && data.source_data) {
							const sourceKey = Object.keys(data.source_data)[0];
							const sourceData = data.source_data[sourceKey];
							return getDataTypeValue(sourceData, dataType) || NaN;
						} else if (data.type === ForeCastType.ACTUAL_PRICE) {
							return getDataTypeValue(data, dataType) || NaN;
						}
						return NaN;
					});
				});
			} else {
				// Maintain alignment with NaN placeholders when chart is on the other axis
				dataTypesToUse.forEach(() => {
					barChartLeftAxisYAccsesor.push(() => NaN);
				});
			}

			if (chart.axis.value === ChartAxisPosition.RIGHT_AXIS) {
				dataTypesToUse.forEach((dataType) => {
					barChartRightAxisYAccsesor.push((d: any) => {
						const localIdx = Math.max(0, fields.indexOf(dataType));
						const seriesIdx = startIndexForChart + localIdx;
						const data = d[`cv${chart.symbol}-${chart.boerse}-${seriesIdx}`];
						if (!data) return NaN;
						if (data.type === ForeCastType.FORECASTED_PRICE && data.source_data) {
							const sourceKey = Object.keys(data.source_data)[0];
							const sourceData = data.source_data[sourceKey];
							return getDataTypeValue(sourceData, dataType) || NaN;
						} else if (data.type === ForeCastType.ACTUAL_PRICE) {
							return getDataTypeValue(data, dataType) || NaN;
						}
						return NaN;
					});
				});
			} else {
				dataTypesToUse.forEach(() => {
					barChartRightAxisYAccsesor.push(() => NaN);
				});
			}
		});

		return { yAxisLeftAccsessor: barChartLeftAxisYAccsesor, yAxisRightAccessor: barChartRightAxisYAccsesor };
	}, [chartSettings, selectedDataTypesPerMaterial, materialIndex]);

	// Partition chartSettings by axis once, used by multiple memoized computations
	const { leftAxisData, rightAxisData } = useMemo(
		() => ({
			leftAxisData: chartSettings.filter((ele) => ele.axis.value === ChartAxisPosition.LEFT_AXIS),
			rightAxisData: chartSettings.filter((ele) => ele.axis.value === ChartAxisPosition.RIGHT_AXIS)
		}),
		[chartSettings]
	);

	// Axis-specific maps of chart.id -> start index within the axis-specific flattened bar yAccessor array
	const barSeriesStartIndexMapLeft = useMemo(() => {
		const map: Record<string, number> = {};
		let cumulativeIndex = 0;
		chartSettings.forEach((chart) => {
			const key = `${chart.symbol}-${chart.boerse}-${materialIndex || 0}`;
			const selected = selectedDataTypesPerMaterial?.[key];
			map[`${chart.id}`] = cumulativeIndex;
			const count =
				selected && selected.length > 0 ? selected.length : Array.isArray(chart.fields) && chart.fields.length > 0 ? chart.fields.length : 1;
			cumulativeIndex += count;
		});
		return map;
	}, [chartSettings, selectedDataTypesPerMaterial, materialIndex]);

	const barSeriesStartIndexMapRight = useMemo(() => {
		const map: Record<string, number> = {};
		let cumulativeIndex = 0;
		chartSettings.forEach((chart) => {
			const key = `${chart.symbol}-${chart.boerse}-${materialIndex || 0}`;
			const selected = selectedDataTypesPerMaterial?.[key];
			map[`${chart.id}`] = cumulativeIndex;
			const count =
				selected && selected.length > 0 ? selected.length : Array.isArray(chart.fields) && chart.fields.length > 0 ? chart.fields.length : 1;
			cumulativeIndex += count;
		});
		return map;
	}, [chartSettings, selectedDataTypesPerMaterial, materialIndex]);

	// Unified global map (axis-agnostic) of chart.id -> start index in flattened cv indices
	const seriesStartIndexMap = useMemo(() => {
		const map: Record<string, number> = {};
		let cumulativeIndex = 0;
		chartSettings.forEach((chart, idx) => {
			const materialKey = `${chart.symbol}-${chart.boerse}-${materialIndex || idx}`;
			map[`${chart.id}`] = cumulativeIndex;
			const selected = selectedDataTypesPerMaterial?.[materialKey];
			const count =
				selected && selected.length > 0 ? selected.length : Array.isArray(chart.fields) && chart.fields.length > 0 ? chart.fields.length : 1;
			cumulativeIndex += count;
		});
		return map;
	}, [chartSettings, selectedDataTypesPerMaterial, materialIndex]);

	const getPercentageChangeYExtentKeys = useMemo(() => {
		const buildKeysForAxis = (axisData: IChartSettingsType[]) => {
			const keys: string[] = [];
			axisData.forEach((item) => {
				const findIndex = chartSettings.findIndex((chart) => chart.id === item.id);
				const materialKey = `${item.symbol}-${item.boerse}-${materialIndex || findIndex}`;
				const fields: string[] = Array.isArray((item as any).fields) ? ((item as any).fields as string[]) : [];
				const selected = selectedDataTypesPerMaterial?.[materialKey];
				const dataTypes = selected && selected.length > 0 ? selected : fields.length > 0 ? [fields[0]] : ['l'];
				const startIndex = seriesStartIndexMap[`${item.id}`] || 0;
				dataTypes.forEach((dt) => {
					const localIdx = Math.max(0, fields.indexOf(dt));
					const seriesIdx = startIndex + localIdx;
					keys.push(`cv${item.symbol}-${item.boerse}-${seriesIdx}`);
				});
			});
			return keys;
		};

		return {
			leftAxisData: buildKeysForAxis(leftAxisData),
			rightAxisData: buildKeysForAxis(rightAxisData)
		};
	}, [chartSettings, leftAxisData, rightAxisData, selectedDataTypesPerMaterial, seriesStartIndexMap, materialIndex]);

	// Utility to check for negative or zero values in y data
	const hasNegativeOrZeroY = (dataArr: IChartData[], yKeys: string[]) => {
		for (const d of dataArr) {
			for (const key of yKeys) {
				const value = d[key];
				if (typeof value === 'object' && value !== null) {
					if ('l' in value && typeof value.l === 'number' && value.l <= 0) return true;
					if ('lp' in value && typeof value.lp === 'number' && value.lp <= 0) return true;
				} else if (typeof value === 'number' && value <= 0) {
					return true;
				}
			}
		}
		return false;
	};

	// yScale logic
	let yScale: any;
	let logScaleHasNegative = false;
	if (scaleType === ScaleType.LOGARITHAMIC_AXIS) {
		const yKeys = [...getPercentageChangeYExtentKeys.leftAxisData, ...getPercentageChangeYExtentKeys.rightAxisData];
		logScaleHasNegative = hasNegativeOrZeroY(data, yKeys);
		if (chartType === 'area') {
			yScale = logScaleHasNegative ? scaleLinear() : scaleLog();
		} else {
			yScale = logScaleHasNegative ? scaleLinear() : scaleLog();
		}
	} else if (scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) {
		yScale = scaleLinear(); // Always allow negative values for percentage change
	} else {
		yScale = scaleLinear();
	}

	let isCompareWithSelfEnabled = false;
	chartSettings.map((chart: IChartSettingsType) => {
		if (chart.history.value !== '') {
			isCompareWithSelfEnabled = true;
		}
	});

	const finalHeight = useMemo(() => containerHeight + calculateHeight, [calculateHeight, containerHeight]);

	// Dynamically adjust font size for y-axis labels based on max digit length
	const getMaxYValueDigits = (data: IChartData[], chartSettings: IChartSettingsType[]) => {
		let maxDigits = 0;

		// Process data in chunks to avoid call stack overflow
		const chunkSize = 1000;
		for (let i = 0; i < data.length; i += chunkSize) {
			const chunk = data.slice(i, i + chunkSize);
			const yValues = chunk.flatMap((item: any) =>
				chartSettings
					.map((chart, idx) => item[`cv${chart.symbol}-${chart.boerse}-${materialIndex || idx}`])
					.filter((v) => v && typeof v.l === 'number')
					.map((v) => v.l)
			);

			// Find max digits in this chunk
			for (const value of yValues) {
				const digitCount = value.toString().replace(/[^0-9]/g, '').length;
				if (digitCount > maxDigits) {
					maxDigits = digitCount;
				}
			}
		}

		return maxDigits;
	};

	const maxYDigits = getMaxYValueDigits(data, chartSettings);
	let dynamicFontSize = cardIndex >= 2 ? 10 : 12;
	if (maxYDigits > 8) {
		dynamicFontSize = 10;
	}

	// Merge dynamic font size into chartDesignConfig
	const mergedChartDesignConfig = {
		...chartDesignConfig,
		fontSize: dynamicFontSize
	};

	// Build yAccessors for percentage-change charts to support multiple selected fields
	const getPercentageYAccessors = useCallback(
		(ele: IChartSettingsType, findIndex: number) => {
			const materialKeyIndex = typeof materialIndex === 'number' ? materialIndex : findIndex;
			const materialKey = `${ele.symbol}-${ele.boerse}-${materialKeyIndex}`;
			const fields: string[] = Array.isArray((ele as any).fields) ? ((ele as any).fields as string[]) : [];
			const selected = selectedDataTypesPerMaterial?.[materialKey];
			const startIndex = seriesStartIndexMap[`${ele.id}`] || 0;

			const toSeriesIdx = (dataType: string) => {
				const localIdx = Math.max(0, fields.indexOf(dataType));
				return startIndex + localIdx;
			};

			if (selected && selected.length > 0) {
				return selected.map((dt) => (d: any) => d.compare[`cv${ele.symbol}-${ele.boerse}-${toSeriesIdx(dt)}`]?.lp);
			}

			// default to first field or 'l'
			const defaultKey = fields.length > 0 ? fields[0] : 'l';
			const defaultSeriesIdx = toSeriesIdx(defaultKey);
			return [(d: any) => d.compare[`cv${ele.symbol}-${ele.boerse}-${defaultSeriesIdx}`]?.lp];
		},
		[selectedDataTypesPerMaterial, seriesStartIndexMap, materialIndex]
	);

	const yExtentRightAxisRange = useMemo(() => {
		if (!data || data.length === 0) return [0, 0];

		const allYValues = data
			.flatMap((d: IChartData) => {
				return getPercentageChangeYExtentKeys.rightAxisData.map((key) => {
					const value = d[key];
					if (value && typeof value === 'object') {
						if ('source_data' in value && value.type !== ForeCastType.ACTUAL_PRICE) {
							const forecastData = value.source_data;
							if (forecastData && typeof forecastData === 'object') {
								const forecastValues = Object.values(forecastData).map((item) => (item as IChartItem)?.l);
								const validValues = forecastValues.filter((v) => v !== undefined && v !== null && !isNaN(v));
								return validValues.length > 0 ? validValues.reduce((acc, val) => Math.max(acc, val), validValues[0]) : undefined;
							}
						} else if ('l' in value) {
							return value.l;
						}
					}
					return undefined;
				});
			})
			.filter((v: number | undefined) => v !== undefined && v !== null && !isNaN(v as number));

		if (allYValues.length === 0) return [0, 0];

		// Use reduce to avoid call stack overflow
		const min = allYValues.reduce((acc: number, val: number) => Math.min(acc, val), allYValues[0]);
		const max = allYValues.reduce((acc: number, val: number) => Math.max(acc, val), allYValues[0]);
		return [min, max];
	}, [data, getPercentageChangeYExtentKeys.rightAxisData]);

	const yExtentLeftAxisRange = useMemo(() => {
		if (!data || data.length === 0) return [0, 0];

		const allYValues = data
			.flatMap((d: IChartData) => {
				return getPercentageChangeYExtentKeys.leftAxisData.map((key) => {
					const value = d[key];
					if (value && typeof value === 'object') {
						if ('source_data' in value && value.type !== ForeCastType.ACTUAL_PRICE) {
							const forecastData = value.source_data;
							if (forecastData && typeof forecastData === 'object') {
								const forecastValues = Object.values(forecastData).map((item) => (item as IChartItem)?.l);
								const validValues = forecastValues.filter((v) => v !== undefined && v !== null && !isNaN(v));
								return validValues.length > 0 ? validValues.reduce((acc, val) => Math.max(acc, val), validValues[0]) : undefined;
							}
						} else if ('l' in value) {
							return value.l;
						}
					}
					return undefined;
				});
			})
			.filter((v: number | undefined) => v !== undefined && v !== null && !isNaN(v as number));

		if (allYValues.length === 0) return [0, 0];

		// Use reduce to avoid call stack overflow
		const min = allYValues.reduce((acc: number, val: number) => Math.min(acc, val), allYValues[0]);
		const max = allYValues.reduce((acc: number, val: number) => Math.max(acc, val), allYValues[0]);
		return [min, max];
	}, [data, getPercentageChangeYExtentKeys.leftAxisData]);

	// Check: when two or more fields are selected, if any selected field has no data in the dataset
	const hasMissingSelectedFieldData = useMemo(() => {
		if (!Array.isArray(chartSettings) || chartSettings.length === 0 || !selectedDataTypesPerMaterial) return false;
		if (!Array.isArray(data) || data.length === 0) return false;

		for (let csIndex = 0; csIndex < chartSettings.length; csIndex++) {
			const cs = chartSettings[csIndex];
			const materialKeyIndex = typeof materialIndex === 'number' ? materialIndex : csIndex;
			const materialKey = `${cs.symbol}-${cs.boerse}-${materialKeyIndex}`;
			const selected = selectedDataTypesPerMaterial[materialKey];
			if (!selected || selected.length < 2) continue;

			const fields = Array.isArray(cs.fields) ? cs.fields : [];
			const startIndex = seriesStartIndexMap[`${cs.id}`] || 0;

			for (let dt = 0; dt < selected.length; dt++) {
				const dataType = selected[dt];
				const localIdx = Math.max(0, fields.indexOf(dataType));
				const seriesIdx = startIndex + localIdx;
				const keyPrefix = `cv${cs.symbol}-${cs.boerse}-${seriesIdx}`;

				let foundAnyValueForThisField = false;
				for (let i = 0; i < data.length && !foundAnyValueForThisField; i++) {
					const row: any = data[i];
					const value = row[keyPrefix];
					if (value && typeof value === 'object') {
						if (value.type === ForeCastType.ACTUAL_PRICE) {
							const v = getDataTypeValue(value as IChartItem, dataType);
							if (typeof v === 'number' && !isNaN(v)) foundAnyValueForThisField = true;
						} else if (value.source_data && typeof value.source_data === 'object') {
							const sources = Object.values(value.source_data) as any[];
							for (let s = 0; s < sources.length && !foundAnyValueForThisField; s++) {
								const v = getDataTypeValue(sources[s] as IChartItem, dataType);
								if (typeof v === 'number' && !isNaN(v)) foundAnyValueForThisField = true;
							}
						}
					}
				}

				if (!foundAnyValueForThisField) {
					return true; // missing data for a selected field
				}
			}
		}

		return false;
	}, [chartSettings, selectedDataTypesPerMaterial, data, seriesStartIndexMap, materialIndex]);

	useEffect(() => {
		if (hasMissingSelectedFieldData || data.length <= 1) {
			setIsExportDisabled && setIsExportDisabled(true);
		} else {
			setIsExportDisabled && setIsExportDisabled(false);
		}
	}, [hasMissingSelectedFieldData]);

	return (
		(chartDetails && !loading && (
			<Fragment key={chartDetails.symbol}>
				{!isEmpty(chartData) && (data.length <= 1 || hasMissingSelectedFieldData) && (
					<div className='d-flex justify-content-center align-items-center' style={{ height: finalHeight }}>
						<Translate text={localizationConstants.noEnoughDataToPlot} />
					</div>
				)}
				{yScale && !isEmpty(chartData) && data.length > 1 && !hasMissingSelectedFieldData && containerWidth !== 0 && finalHeight !== 0 && (
					<ChartCanvas
						width={containerWidth}
						height={staticHeight ? 550 : finalHeight}
						seriesName={`MSFT_${suffix}`}
						data={data}
						type={'svg'}
						xAccessor={xAccessor}
						xScale={xScale}
						displayXAccessor={displayXAccessor}
						xExtents={xExtents}
						clamp={true}
						margin={chartDesignConfig?.margin}
						postCalculator={compareCalculator}
						ratio={1}
						mouseMoveEvent={true}
						panEvent={!isIframe}
						zoomEvent={!(!isZoomEnabled || isIframe)}
						pointsPerPxThreshold={100}
						padding={!isEmpty(barWidth) && barWidth[`${symbol}-${boerse}`] ? barWidth[`${symbol}-${boerse}`] : 0}
						className={`react-stockchart-${chartName}`}
						zoomMultiplier={span === '1w' ? 2 : 1.1}
						chartView={scaleType}
						useCrossHairStyleCursor={!isIframe}
					>
						<defs>
							{chartSettings.map((chart) => (
								<Fragment key={`${chart.id}-${cardIndex}`}>
									<linearGradient id={`bar${chart.id}-${cardIndex}`} x1='0%' y1='0%' x2='0%' y2='100%'>
										<stop offset='0%' stopColor={chart.color} />
										<stop offset='100%' stopColor={chart.color} stopOpacity='0' />
									</linearGradient>
									<linearGradient
										id={`MyGradient${chart.symbol}-${chart.boerse}${chart.id}-${cardIndex}`}
										x1='100%'
										y1='-50%'
										x2='100%'
										y2='100%'
										gradientUnits='userSpaceOnUse'
									>
										<stop offset='0.326661' stopColor={chart.color} />
										<stop offset='1' stopColor={chart.color} stopOpacity='0' />
									</linearGradient>
									<linearGradient id={`MyGradientForecast`} x1='100%' y1='-50%' x2='100%' y2='100%' gradientUnits='userSpaceOnUse'>
										<stop offset='0.326661' stopColor='#FF9100' />
										<stop offset='1' stopColor='#FF9100' stopOpacity='0' />
									</linearGradient>
									{!isEmpty(allChartDetails) && allChartDetails[`${symbol}-${boerse}-0`]?.sources && chartType !== 'bar' ? (
										allChartDetails[`${symbol}-${boerse}-0`]?.sources?.map((item, index) => {
											return (
												<linearGradient
													id={`MyGradientListForecast${index}`}
													x1='100%'
													y1='-50%'
													x2='100%'
													y2='100%'
													gradientUnits='userSpaceOnUse'
												>
													<stop offset='0.326661' stopColor={`${colorList[item]}`} />
													<stop
														offset={index > 0 ? '0.55' : '1'}
														stopColor={`${colorList[item]}`}
														stopOpacity={index === 1 ? '0.3' : index === 2 ? '0.2' : '0'}
													/>
												</linearGradient>
											);
										})
									) : (
										<linearGradient
											id={`MyGradientForecast`}
											x1='100%'
											y1='-50%'
											x2='100%'
											y2='100%'
											gradientUnits='userSpaceOnUse'
										>
											<stop offset='0.326661' stopColor='#FF9100' />
											<stop offset='1' stopColor='#FF9100' stopOpacity='0' />
										</linearGradient>
									)}
									{!isEmpty(allChartDetails) && allChartDetails[`${symbol}-${boerse}-0`]?.sources && chartType === 'bar' ? (
										allChartDetails[`${symbol}-${boerse}-0`]?.sources?.map((item, index) => {
											return (
												<linearGradient
													id={`MyGradientListBarForecast${index}`}
													x1='0%'
													y1='50%'
													x2='100%'
													y2='0%'
													gradientUnits='userSpaceOnUse'
												>
													<stop offset='0.32665' stopColor={`${colorList[item]}`} />
													<stop
														offset={index > 0 ? '0.55' : '1'}
														stopColor={`${colorList[item]}`}
														stopOpacity={index === 1 ? '0.3' : index === 2 ? '0.2' : '0'}
													/>
												</linearGradient>
											);
										})
									) : (
										<linearGradient
											id={`MyGradientForecast`}
											x1='100%'
											y1='-50%'
											x2='100%'
											y2='100%'
											gradientUnits='userSpaceOnUse'
										>
											<stop offset='0.326661' stopColor='#FF9100' />
											<stop offset='1' stopColor='#FF9100' stopOpacity='0' />
										</linearGradient>
									)}
									<linearGradient id='MyGradientListForecast0' x1='0%' y1='10%' x2='90%' y2='0%' gradientUnits='userSpaceOnUse'>
										<stop offset='0.8' stop-color='#FF9100' />
										<stop offset='1' stop-color='#FF9100' stop-opacity='0' />
									</linearGradient>
								</Fragment>
							))}
						</defs>
						{(scaleType === ScaleType.PRICE_AXIS || scaleType === ScaleType.LOGARITHAMIC_AXIS) && !isEmpty(rightAxisData) && (
							<Chart
								id={`${chartDetails.symbol}-${chartDetails.boerse}-${cardIndex}-1`}
								yExtents={yExtentRightAxisRange}
								height={containerHeight - chartTopPadding}
								yScale={yScale}
							>
								<>
									{rightAxisData.length > 0 && (
										<ChartAxis
											tickFormat={(d: number) => customNumberFormatter(d, chartDetails?.nkstellen)}
											chartType={chartType}
											data={data}
											span={span as string}
											axisAt={ChartAxisPosition.RIGHT_AXIS}
											chartDesignConfig={mergedChartDesignConfig}
											yTicks={renderTicks(cardIndex, chartType === 'bar', chartName).yTicks}
											xTicks={renderTicks(cardIndex, chartType === 'bar', chartName).xTicks}
											isCompareWithSelfEnabled={isCompareWithSelfEnabled}
											yAxisFontSize={yAxisFontSize ?? dynamicFontSize}
											xAxisFontSize={xAxisFontSize ?? mergedChartDesignConfig.fontSize}
										/>
									)}
									{rightAxisData.length > 0 &&
										rightAxisData.map((ele) => (
											<CommonChart
												cardIndex={cardIndex}
												key={ele.id}
												chartSettings={chartSettings}
												chartDetails={chartDetails}
												chartType={chartType}
												ele={ele}
												getBarChartYAccsesor={getBarChartYAccsesor.yAxisRightAccessor}
												materialIndex={materialIndex}
												setBarWidth={setBarWidth}
												strokeColor={
													allChartDetails[`${symbol}-${boerse}-0`]?.sources !== undefined &&
													allChartDetails[`${symbol}-${boerse}-0`]?.sources.map((item) => colorList[item])
												}
												colorList={colorList}
												data={data}
												forecastKeys={forecastKeys || []}
												barSeriesStartIndexMap={barSeriesStartIndexMapRight}
												selectedKeys={
													selectedDataTypesPerMaterial &&
													selectedDataTypesPerMaterial[
														`${ele.symbol}-${ele.boerse}-${
															materialIndex || chartSettings.findIndex((c) => c.id === ele.id)
														}`
													]
												}
											/>
										))}
								</>
								{showZoomButton && chartType !== 'bar' && (
									<ZoomButtons
										onReset={() => {
											setSuffix(suffix + 1);
										}}
									/>
								)}
							</Chart>
						)}
						{(scaleType === ScaleType.PRICE_AXIS || scaleType === ScaleType.LOGARITHAMIC_AXIS) && !isEmpty(leftAxisData) && (
							<Chart
								id={`${chartDetails.symbol}-${chartDetails.boerse}-${cardIndex}-2`}
								yExtents={yExtentLeftAxisRange}
								height={containerHeight - chartTopPadding}
								yScale={yScale}
							>
								<>
									{leftAxisData.length > 0 && (
										<ChartAxis
											tickFormat={(d: number) => customNumberFormatter(d, chartDetails?.nkstellen)}
											chartType={chartType}
											data={data}
											span={span}
											axisAt={ChartAxisPosition.LEFT_AXIS}
											chartDesignConfig={mergedChartDesignConfig}
											yTicks={renderTicks(cardIndex, chartType === 'bar', chartName).yTicks}
											xTicks={renderTicks(cardIndex, chartType === 'bar', chartName).xTicks}
											isCompareWithSelfEnabled={isCompareWithSelfEnabled}
											yAxisFontSize={yAxisFontSize ?? dynamicFontSize}
											xAxisFontSize={xAxisFontSize ?? mergedChartDesignConfig.fontSize}
										/>
									)}
									{leftAxisData.length > 0 &&
										leftAxisData.map((ele) => {
											return (
												<CommonChart
													cardIndex={cardIndex}
													key={ele.id}
													chartSettings={chartSettings}
													chartDetails={chartDetails}
													chartType={chartType}
													ele={ele}
													getBarChartYAccsesor={getBarChartYAccsesor.yAxisLeftAccsessor}
													materialIndex={materialIndex}
													setBarWidth={setBarWidth}
													strokeColor={allChartDetails[`${symbol}-${boerse}-0`]?.sources?.map((item) => colorList[item])}
													colorList={colorList}
													data={data}
													forecastKeys={forecastKeys || []}
													barSeriesStartIndexMap={barSeriesStartIndexMapLeft}
													selectedKeys={
														selectedDataTypesPerMaterial &&
														selectedDataTypesPerMaterial[
															`${ele.symbol}-${ele.boerse}-${
																materialIndex || chartSettings.findIndex((c) => c.id === ele.id)
															}`
														]
													}
												/>
											);
										})}
								</>
							</Chart>
						)}
						{(scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) && !isEmpty(leftAxisData) && (
							<Chart
								id={`${symbol}-${boerse}-${cardIndex}-3`}
								yExtents={(d: any) => {
									const values = getPercentageChangeYExtentKeys.leftAxisData
										.map((key) => {
											return d.compare[key]?.lp;
										})
										.filter((v) => v !== undefined);

									// If all values are zero or no values, return [-0.05, 0.05] to center the line
									if (values.length === 0 || values.every((v) => v === 0)) {
										return [-0.05, 0.05];
									}

									// If there's only one value and it's zero, center it
									if (values.length === 1 && values[0] === 0) {
										return [-0.05, 0.05];
									}
									return [...values];
								}}
								height={containerHeight - chartTopPadding}
							>
								<>
									{leftAxisData.length > 0 && (
										<ChartAxis
											tickFormat={d3.format('.2%')}
											chartType={chartType}
											data={data}
											span={span}
											axisAt={ChartAxisPosition.LEFT_AXIS}
											chartDesignConfig={chartDesignConfig}
											yTicks={renderTicks(cardIndex, chartType === 'bar', chartName).yTicks}
											xTicks={renderTicks(cardIndex, chartType === 'bar', chartName).xTicks}
											isCompareWithSelfEnabled={isCompareWithSelfEnabled}
											yAxisFontSize={yAxisFontSize ?? dynamicFontSize}
											xAxisFontSize={xAxisFontSize ?? chartDesignConfig.fontSize}
										/>
									)}
									{chartType !== 'bar' &&
										leftAxisData.map((ele) => {
											const findIndex = chartSettings.findIndex((chart) => chart.id === ele.id);
											const yAccessors = getPercentageYAccessors(ele, findIndex);
											const fills = yAccessors.map(() => `url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`);
											const strokes = yAccessors.map(() => ele.color);
											return (
												<Fragment key={ele.id}>
													{chartDetails.isIntraday && (
														<RenderChart
															type={chartType}
															yAccessors={yAccessors}
															fills={fills}
															stroke={strokes}
															selectedDataTypes={
																selectedDataTypesPerMaterial &&
																selectedDataTypesPerMaterial[
																	`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																]
															}
														/>
													)}
													{!chartDetails.isIntraday && (
														<>
															<RenderChart
																type={chartType}
																yAccessors={yAccessors}
																fills={fills}
																stroke={strokes}
																selectedDataTypes={
																	selectedDataTypesPerMaterial &&
																	selectedDataTypesPerMaterial[
																		`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																	]
																}
															/>
															{chartSettings.length === 1 && (
																<>
																	{chartDetails.sources && (
																		<RenderChart
																			type={chartType}
																			yAccessors={[
																				...Object.keys(colorList).map((key) => (d: any) => {
																					const actualPriceData = data.filter(
																						(item: any) =>
																							item[`cv${ele.symbol}-${ele.boerse}-${0}`]?.type ===
																							ForeCastType.ACTUAL_PRICE
																					);
																					const actualPriceLastData =
																						actualPriceData[actualPriceData.length - 1];
																					const lastActualPriceDate = !isEmpty(actualPriceData)
																						? actualPriceLastData?.date
																						: new Date();
																					const chartData = d[`cv${ele.symbol}-${ele.boerse}-0`];
																					const sourceData =
																						d[
																							`cv${ele.symbol}-${ele.boerse}-${
																								materialIndex || findIndex
																							}`
																						]?.source_data;

																					// Return the 'lp' value for the key, only if the key exists in the source data
																					if (chartData?.type === ForeCastType.FORECASTED_PRICE) {
																						return sourceData?.[key]?.lp;
																					} else if (lastActualPriceDate === d?.date) {
																						return actualPriceLastData[`cv${ele.symbol}-${ele.boerse}-0`]
																							?.lp;
																					}
																				})
																			]}
																			fills={[
																				...Object.keys(colorList).map((key, idx) => {
																					// Dynamically map fills to predefined gradient URLs
																					return `url(#MyGradientListForecast${idx})`; // Assuming each gradient has an index (0, 1, 2)
																				})
																			]}
																			stroke={allChartDetails[`${symbol}-${boerse}-0`]?.sources.map(
																				(item) => colorList[item]
																			)}
																			chartDetails={chartDetails}
																			selectedDataTypes={
																				selectedDataTypesPerMaterial &&
																				selectedDataTypesPerMaterial[
																					`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																				]
																			}
																		/>
																	)}
																</>
															)}
														</>
													)}
													{ele.indicators.map((indi, index) => {
														return (
															<RenderIndicatorsOnchart
																key={`${ele.id}-${indi.value}`}
																index={index}
																type={indi.value}
																color={ele.color}
																yAccessor={(d: any) => {
																	if (d[`cv${ele.symbol}-${ele.boerse}-0`]?.type === 'actualPrice') {
																		return d.compare[`${indi.value}-${ele.symbol}-${ele.boerse}-${ele.id}`];
																	} else {
																		return null;
																	}
																}}
															/>
														);
													})}
												</Fragment>
											);
										})}
								</>
							</Chart>
						)}
						{(scaleType === ScaleType.PERCENTAGE_CHANGE || scaleType === ScaleType.PERCENTAGE_CHANGE_TOTAL) &&
							!isEmpty(rightAxisData) && (
								<Chart
									id={`${symbol}-${boerse}-${cardIndex}-4`}
									yExtents={(d: any) => {
										const values = getPercentageChangeYExtentKeys.rightAxisData
											.map((key) => {
												return d.compare[key]?.lp;
											})
											.filter((v) => v !== undefined);

										// If all values are zero or no values, return [-0.05, 0.05] to center the line
										if (values.length === 0 || values.every((v) => v === 0)) {
											return [-0.05, 0.05];
										}

										// If there's only one value and it's zero, center it
										if (values.length === 1 && values[0] === 0) {
											return [-0.05, 0.05];
										}
										return [...values];
									}}
									height={containerHeight - chartTopPadding}
								>
									<>
										{rightAxisData.length > 0 && (
											<ChartAxis
												tickFormat={d3.format('.2%')}
												chartType={chartType}
												data={data}
												span={span}
												axisAt={ChartAxisPosition.RIGHT_AXIS}
												chartDesignConfig={chartDesignConfig}
												yTicks={renderTicks(cardIndex, chartType === 'bar', chartName).yTicks}
												xTicks={renderTicks(cardIndex, chartType === 'bar', chartName).xTicks}
												isCompareWithSelfEnabled={isCompareWithSelfEnabled}
												yAxisFontSize={yAxisFontSize ?? dynamicFontSize}
												xAxisFontSize={xAxisFontSize ?? chartDesignConfig.fontSize}
											/>
										)}
										{chartType !== 'bar' &&
											rightAxisData.map((ele) => {
												const findIndex = chartSettings.findIndex((chart) => chart.id === ele.id);
												const yAccessors = getPercentageYAccessors(ele, findIndex);
												const fills = yAccessors.map(
													() => `url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`
												);
												const strokes = yAccessors.map(() => ele.color);
												return (
													<Fragment key={ele.id}>
														{chartDetails.isIntraday && (
															<RenderChart
																type={chartType}
																yAccessors={yAccessors}
																fills={fills}
																stroke={strokes}
																selectedDataTypes={
																	selectedDataTypesPerMaterial &&
																	selectedDataTypesPerMaterial[
																		`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																	]
																}
															/>
														)}
														{!chartDetails.isIntraday && (
															<>
																<RenderChart
																	type={chartType}
																	yAccessors={yAccessors}
																	fills={fills}
																	stroke={strokes}
																	selectedDataTypes={
																		selectedDataTypesPerMaterial &&
																		selectedDataTypesPerMaterial[
																			`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																		]
																	}
																/>
																{chartSettings.length === 1 && (
																	<>
																		{chartDetails.sources && (
																			<RenderChart
																				type={chartType}
																				yAccessors={[
																					...Object.keys(colorList).map((key) => (d: any) => {
																						const actualPriceData = data.filter(
																							(item: any) =>
																								item[`cv${ele.symbol}-${ele.boerse}-${0}`]?.type ===
																								ForeCastType.ACTUAL_PRICE
																						);
																						const actualPriceLastData =
																							actualPriceData[actualPriceData.length - 1];
																						const lastActualPriceDate = actualPriceLastData?.date;
																						const chartData = d[`cv${ele.symbol}-${ele.boerse}-0`];
																						const sourceData =
																							d[
																								`cv${ele.symbol}-${ele.boerse}-${
																									materialIndex || findIndex
																								}`
																							]?.source_data;

																						// Return the 'lp' value for the key, only if the key exists in the source data
																						if (chartData.type === ForeCastType.FORECASTED_PRICE) {
																							return sourceData?.[key]?.lp;
																						} else if (lastActualPriceDate === d?.date) {
																							return actualPriceLastData[
																								`cv${ele.symbol}-${ele.boerse}-0`
																							]?.lp;
																						}
																					})
																				]}
																				fills={[
																					...Object.keys(colorList).map((key, idx) => {
																						// Dynamically map fills to predefined gradient URLs
																						return `url(#MyGradientListForecast${idx})`; // Assuming each gradient has an index (0, 1, 2)
																					})
																				]}
																				stroke={allChartDetails[`${symbol}-${boerse}-0`]?.sources.map(
																					(item) => colorList[item]
																				)}
																				chartDetails={chartDetails}
																				selectedDataTypes={
																					selectedDataTypesPerMaterial &&
																					selectedDataTypesPerMaterial[
																						`${ele.symbol}-${ele.boerse}-${materialIndex || findIndex}`
																					]
																				}
																			/>
																		)}
																	</>
																)}
															</>
														)}
														{ele.indicators.map((indi, index) => {
															return (
																<RenderIndicatorsOnchart
																	key={`${ele.id}-${indi.value}`}
																	index={index}
																	type={indi.value}
																	color={ele.color}
																	yAccessor={(d: any) => {
																		if (d[`cv${ele.symbol}-${ele.boerse}-0`]?.type === 'actualPrice') {
																			return d.compare[`${indi.value}-${ele.symbol}-${ele.boerse}-${ele.id}`];
																		} else {
																			return null;
																		}
																	}}
																/>
															);
														})}
													</Fragment>
												);
											})}
									</>
								</Chart>
							)}
						{chartSettings.length > 0 &&
							chartSettings.map((chart, index) => {
								const externalChartIndicators = chart.indicators.filter((item) => ['macd', 'sto', 'atr', 'rsi'].includes(item.value));
								return externalChartIndicators.map((indi) => {
									return RenderExternalIndicators({
										indi,
										chart,
										cardIndex,
										chartType,
										chartDesignConfig,
										chartSettings,
										scaleType,
										index,
										data,
										span: span,
										isCompareWithSelfEnabled,
										chartName
									});
								});
							})}
						{!isIframe && <CrossHairCursor fontSize={chartDesignConfig?.fontSize} strokeDasharray='Solid' />}
						{showZoomButton && (
							<HoverTooltip
								tooltipContent={toolTipContent}
								fontSize={chartDesignConfig?.fontSize}
								fontFill='#081D34'
								fontFamily='Outfit'
								bgFill='transparent'
								backgroundShapeSVG={(data: any, item: any) => {
									return <TooltipBackgroundShapeSVG item={item} />;
								}}
								tooltipSVG={(data: any, content: any) => {
									return <HoverTooltipSVG data={data} content={content} />;
								}}
								isValueBlur={chartDetails?.forecastAccess === 0 ? true : false}
							/>
						)}
						{chartName === 'main' && (
							<Label
								x={
									containerWidth -
									(size(chartSettings) > 1 &&
									!isEmpty(chartSettings.filter((item) => item.axis.value === 'left')) &&
									chartSettings.filter((item) => item.axis.value === 'left').length !== chartSettings.length
										? 220
										: 150)
								}
								y={containerHeight - 60}
								fontSize={25}
								text='MATFLIXX'
								fill='#507dcb'
								style={{ zIndex: 10 }}
							/>
						)}
					</ChartCanvas>
				)}
				{action === 'openChartColorPopup' && (
					<ChangeChartColorPopup handleClose={() => setAction('')} handleSubmit={handleChangeChartColor} />
				)}
			</Fragment>
		)) || <></>
	);
};

export const RenderChart: React.FC<{
	type: string;
	yAccessor?: (d: any) => any;
	yAccessors?: Array<(d: any) => any>;
	fill?: string;
	fills?: string[];
	stroke: string | string[];
	canvasGradient?: any;
	chartDetails?: IChartDetails;
	selectedDataTypes?: string[];
}> = ({ type, yAccessor, yAccessors, fill, fills = [], stroke, canvasGradient, chartDetails, selectedDataTypes }) => {
	const location = useLocation();
	const isIframe = location.pathname.includes('iframe');
	const isForecastValue = chartDetails && chartDetails?.forecastAccess === 0;
	const svgRef = useRef<SVGSVGElement>(null);

	// Determine if multiple accessors are used
	const useMultipleAccessors = Array.isArray(yAccessors) && yAccessors.length > 0;

	// Handle backward compatibility for single accessor/fill
	const accessorArray = useMultipleAccessors ? yAccessors : [yAccessor];
	const fillArray = useMultipleAccessors ? fills : [fill || 'none'];
	const strokeArray = Array.isArray(stroke) ? stroke : [stroke];

	// Add CSS styles for animations
	const styles = `
        @keyframes drawLine {
            0% {
                stroke-dasharray: 1000000;
                stroke-dashoffset: 1000000;
                opacity: 0;
                clip-path: inset(100% 0 0 0);
            }
            100% {
                stroke-dasharray: 1000000;
                stroke-dashoffset: 0;
                opacity: 1;
                clip-path: inset(0 0 0 0);
            }
        }
        @keyframes fillFromXAxis {
            0% {
                opacity: 0;
                transform: scaleY(0);
                transform-origin: bottom;
            }
            100% {
                opacity: 1;
                transform: scaleY(1);
                transform-origin: bottom;
            }
        }
        .line-animation {
            animation: drawLine 2s ease-out forwards;
            stroke-linecap: round;
            stroke-linejoin: round;
            transform-origin: bottom;
            transform-box: fill-box;
            will-change: clip-path;
        }
        .area-animation {
            animation: fillFromXAxis 2s ease-out forwards;
            transform-box: fill-box;
			transform-origin: bottom;
        }
        .forecast-blur {
            filter: blur(2px);
            opacity: 0.7;
        }
        /* Disable animations during export */
        [data-export-mode="true"] .line-animation,
        [data-export-mode="true"] .area-animation {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
            clip-path: none !important;
        }
    `;

	const isExportMode = () => {
		const chartContainer = document.querySelector('[data-export-mode="true"]');
		return !!chartContainer;
	};

	const shouldDisableAnimations = () => {
		return location.pathname.includes('excel/export') || isExportMode();
	};

	return (
		<>
			<style>{styles}</style>
			{type === 'area' &&
				accessorArray.map((accessor, index) => {
					const shouldShowMultipleDataTypes = selectedDataTypes && selectedDataTypes.length > 0;
					const baseColor = strokeArray[index] || '#4472c4';
					const chartGroupingColor = getColorForDataType(baseColor, index, selectedDataTypes || []);

					return (
						<Fragment key={index}>
							<AreaSeries
								yAccessor={accessor}
								fill={shouldShowMultipleDataTypes ? chartGroupingColor : fillArray[index] || 'none'}
								stroke={shouldShowMultipleDataTypes ? chartGroupingColor : strokeArray[index] || 'none'}
								strokeWidth={1}
								interpolation={curveMonotoneX}
								canvasGradient={canvasGradient}
								clip={false}
								className={`${!shouldDisableAnimations() && 'area-animation'}  ${isForecastValue ? 'forecast-blur' : ''}`}
								svgRef={svgRef}
							/>
							{!isIframe && <CurrentCoordinate yAccessor={accessor} fill={fillArray[index] || strokeArray[index]} />}
						</Fragment>
					);
				})}
			{type === 'line' &&
				accessorArray.map((accessor, index) => {
					const shouldShowMultipleDataTypes = selectedDataTypes && selectedDataTypes.length > 0;
					const baseColor = strokeArray[index] || '#4472c4';
					const chartGroupingColor = getColorForDataType(baseColor, index, selectedDataTypes || []);

					return (
						<Fragment key={index}>
							<LineSeries
								yAccessor={accessor}
								stroke={shouldShowMultipleDataTypes ? chartGroupingColor : strokeArray[index] || 'none'}
								strokeWidth={2}
								clip={false}
								interpolation={curveMonotoneX}
								className={`${!shouldDisableAnimations() && 'line-animation'} ${isForecastValue ? 'forecast-blur' : ''}`}
								svgRef={svgRef}
							/>
							{!isIframe && <CurrentCoordinate yAccessor={accessor} fill={strokeArray[index]} />}
						</Fragment>
					);
				})}
		</>
	);
};

// const StockChart = fitWidth(StockChResponsiveStockChart);

export default StockChart;
